import React, { useEffect, useState } from 'react'
import {
  Select,
  Card,
  Row,
  Col,
  Divider,
  Spin,
  Empty,
  Table,
  Button,
  Space,
} from 'antd'
import axiosInstance from '../../Utils/axiosInstance'
import { SlackOutlined } from '@ant-design/icons'
import {
  GetProductDataAPI,
  StockReportAPI,
  GetStocksDataAPI,
} from '../../AllAPI/AdminAPI'
import dayjs from 'dayjs'

const { Option } = Select

const Stock = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [mode, setMode] = useState('all') // product | all
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [product, setProduct] = useState(null)
  const [sellData, setSellData] = useState(null)
  const [allStock, setAllStock] = useState([])
  const [allStockLoading, setAllStockLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [showBtnLoading, setShowBtnLoading] = useState(false)

  /* ================= AUTO CALL ALL STOCK ================= */
  useEffect(() => {
    if (mode === 'all') {
      fetchAllStock()
    }
  }, [mode])

  /* ================= Load Products ================= */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axiosInstance.get(`${GetProductDataAPI}/${selectedYear}`)
        setProducts(res.data || [])
      } catch (err) {
        console.error(err)
      }
    }
    fetchProducts()
  }, [])

  /* ================= Mode Change ================= */
  const handleModeChange = (value) => {
    setMode(value)
    setProduct(null)
    setSellData(null)
    setSelectedProduct(null)
  }

  /* ================= Product Select ================= */
  const handleProductSelect = (value) => {
    setSelectedProduct(value)
    setProduct(null)
    setSellData(null)
  }

  /* ================= Show Button ================= */
  const handleShow = async () => {
    if (!selectedProduct) return

    try {
      setShowBtnLoading(true)
      setProductLoading(true)
      const res = await axiosInstance.get(
        `${StockReportAPI}/${selectedProduct}/${selectedYear}`,
      )
      // 👇 IMPORTANT PART
      setProduct(res.data?.productInfo || null)
      setSellData(res.data?.sales_data || null)
    } catch (err) {
      console.error(err)
      setProduct(null)
      setSellData(null)
    } finally {
      setShowBtnLoading(false)
      setProductLoading(false)
    }
  }

  /* ================= Fetch ALL Stock ================= */
  const fetchAllStock = async () => {
    try {
      setAllStockLoading(true)
      const res = await axiosInstance.get(`${GetStocksDataAPI}/${selectedYear}`)

      const formatted = Array.isArray(res.data)
        ? res.data.map((item) => ({
            id: item.id,
            product_name: item.product_name,
            size: item.product_size,
            product_qty: item.total_colored,
            delivered_qty: item.total_delivered,
            breakage_qty: item.total_breakage,
            stock_qty: item.current_stock,
          }))
        : []

      setAllStock(formatted)
    } finally {
      setAllStockLoading(false)
    }
  }

  /* ================= Calculations ================= */
  const totalOrder =
    product?.orders?.reduce((a, b) => a + b.approval_quantity, 0) || 0

  const totalColoring =
    product?.colorings?.reduce((a, b) => a + b.quantity, 0) || 0

  const totalDelivered =
    product?.delivery_details?.reduce((a, b) => a + b.quantity, 0) || 0

  const totalBreakage =
    product?.breakageProduct?.reduce((a, b) => a + b.breakage_quantity, 0) || 0

  const totalSell = sellData || 0

  const currentStock = totalColoring - (totalDelivered + totalBreakage)

  return (
    <div className="mainContainer">
      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Stock
        </h3>
      </div>

      {/* ================= CONTROLS ================= */}
      <div className="row m-0">
        <div className="col-12 col-md-3 mb-2">
          <label>Option : </label>
          <Select value={mode} className="w-100" onChange={handleModeChange}>
            <Option value="all">All</Option>
            <Option value="product">Product Wise</Option>
          </Select>
        </div>

        {mode === 'product' && (
          <>
            <div className="col-12 col-md-4 mb-2">
              <label>Product : </label>
              <Select
                className="w-100"
                placeholder="Select Product"
                allowClear
                onChange={handleProductSelect}
              >
                {products.map((p) => (
                  <Option key={p.prod_id} value={p.prod_id}>
                    {p.product_name} ({p.size})
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-4 mb-2 d-flex align-items-end">
              <Button
                type="primary"
                className="mt-2"
                onClick={handleShow}
                loading={showBtnLoading}
                disabled={!selectedProduct}
              >
                Show
              </Button>
            </div>
          </>
        )}
      </div>

      <Spin spinning={productLoading}>
        <div
          className="p-1 p-md-3 rounded shadow"
          style={{ border: '2px solid #d1d1d1', backgroundColor: 'white' }}
        >
          {/* ================= ALL STOCK ================= */}
          {mode === 'all' && (
            <Table
              loading={allStockLoading}
              bordered
              rowKey="id"
              className="shadow"
              dataSource={allStock}
              style={{ overflowX: 'auto' }}
              columns={[
                {
                  title: 'Sr No',
                  align: 'center',
                  render: (_, __, index) => index + 1,
                },
                {
                  title: 'Product',
                  render: (_, r) => `${r.product_name} (${r.size})`,
                },
                {
                  title: 'Product Qty (A)',
                  dataIndex: 'product_qty',
                  onCell: () => ({
                    style: { textAlign: 'right' },
                  }),
                },
                {
                  title: 'Deliver Qty (B)',
                  dataIndex: 'delivered_qty',
                  onCell: () => ({
                    style: { textAlign: 'right' },
                  }),
                },
                {
                  title: 'Breakage Qty (C)',
                  dataIndex: 'breakage_qty',
                  onCell: () => ({
                    style: { textAlign: 'right' },
                  }),
                },
                {
                  title: 'Stock  [A - (B + C)]',
                  dataIndex: 'stock_qty',
                  onCell: () => ({
                    style: { textAlign: 'right' },
                  }),
                },
              ]}
            />
          )}

          {/* ================= PRODUCT WISE ================= */}
          {mode === 'product' && !product && (
            <Empty description="Select product and click Show" />
          )}

          {mode === 'product' && product && (
            <>
              <Card
                title="Product Details"
                className="shadow"
                style={{ marginBottom: 20 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12} xl={12}>
                    <b>Name:</b> {product.product_name}
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <b>Size:</b> {product.size}
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <b>Short Name:</b> {product.short_name}
                  </Col>
                </Row>
              </Card>

              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#dbeeff' }}
                    className="shadow mb-3"
                    title="Colored Qty"
                  >
                    {totalColoring}
                  </Card>
                </Col>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#eedbff' }}
                    className="shadow mb-3"
                    title="Breakage Qty"
                  >
                    {totalBreakage}
                  </Card>
                </Col>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#e4ffd3' }}
                    className="shadow mb-3"
                    title="Order Qty"
                  >
                    {totalOrder}
                  </Card>
                </Col>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#ffe3be' }}
                    className="shadow mb-3"
                    title="Delivered Qty"
                  >
                    {totalDelivered}
                  </Card>
                </Col>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#befffa' }}
                    className="shadow mb-3"
                    title="Sell Qty"
                  >
                    {totalSell}
                  </Card>
                </Col>
                <Col xs={24} xl={6}>
                  <Card
                    style={{ background: '#ffd7d6' }}
                    className="shadow mb-3"
                    title="In Stock Qty"
                  >
                    {currentStock}
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                {[
                  {
                    title: 'Raw Idol Production',
                    data: product.rawIdolProductions,
                    qty: 'quantity',
                    date: 'date',
                  },
                  {
                    title: 'Coloring',
                    data: product.colorings,
                    qty: 'quantity',
                    date: 'date',
                  },
                  {
                    title: 'Orders',
                    data: product.orders,
                    qty: 'approval_quantity',
                    date: 'createdAt',
                  },
                  {
                    title: 'Delivery',
                    data: product.delivery_details,
                    qty: 'quantity',
                    date: 'createdAt',
                  },
                  {
                    title: 'Breakage',
                    data: product.breakageProduct,
                    qty: 'breakage_quantity',
                    date: 'date',
                  },
                ].map((t, i) => (
                  <Col xs={24} md={12} xl={6} key={i}>
                    <Divider>{t.title}</Divider>
                    {t.data.length === 0 ? (
                      <Empty />
                    ) : (
                      <Table
                        bordered
                        className="shadow"
                        style={{ overflowX: 'auto' }}
                        pagination={false}
                        rowKey="id"
                        dataSource={t.data}
                        columns={[
                          {
                            title: 'Date',
                            dataIndex: t.date,
                            render: (v) =>
                              v ? dayjs(v).format('DD-MM-YYYY') : '-',
                          },
                          {
                            title: 'Quantity',
                            dataIndex: t.qty,
                            align: 'right',
                          },
                        ]}
                        summary={(pageData) => {
                          const total = pageData.reduce(
                            (sum, row) => sum + Number(row[t.qty] || 0),
                            0,
                          )

                          return (
                            <Table.Summary.Row>
                              <Table.Summary.Cell>
                                <b>Total</b>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right">
                                <b>{total}</b>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )
                        }}
                      />
                    )}
                  </Col>
                ))}
              </Row>
            </>
          )}
        </div>
      </Spin>
    </div>
  )
}

export default Stock
